import React, { useEffect, useMemo, useRef, useState } from 'react';
import kkpService from '../../services/kkpService';
import { confirmVenteClear } from '../../utils/confirmVenteDelete';
import useAppConfirm from '../../hooks/useAppConfirm';
import { filterStatutsForMonth } from '../../utils/kkpStatutUtils';
import { briefCreatifToPayload, venteBriefFromApi } from '../../utils/kkpVenteBriefUtils';
import { briefPiecesJointesFromVente } from '../../utils/kkpBriefFichierUtils';
import {
  filterSemaineOptionsForPeriod,
  generateNewsletterYearWeekOptions,
  resolveKeptSemaine,
} from '../../utils/kkpPlanningSemaineOptions';
import { useKkpVenteBriefWizard } from '../../hooks/useKkpVenteBriefWizard';
import KkpPlanningSemaineSelect from './KkpPlanningSemaineSelect';
import KkpClientAutocomplete from './KkpClientAutocomplete';
import KkpVenteWizardSteps from './KkpVenteWizardSteps';
import KkpVenteBriefCreativeStep from './KkpVenteBriefCreativeStep';
import KkpVenteWizardFooter from './KkpVenteWizardFooter';
import {
  AppFormField,
  AppInput,
  AppModal,
  AppSelect,
} from '../ui';
import {
  batValideOptionTitle,
  interceptGraphisteBatValideRequest,
  interceptLockedBatValideChange,
  isBatValideOptionLocked,
} from '../../utils/kkpBatValide';
import '../../styles/kkp-vente-wizard.scss';

const EMPTY_FORM = {
  annee: new Date().getFullYear(),
  mois: new Date().getMonth() + 1,
  semaine: '',
  commercial_id: '',
  client_label: '',
  entreprise_locale_id: '',
  dolibarr_thirdparty_id: '',
  emplacement: 'NEWS SORTIES WE / BANDEAU TETE',
  type_news: 'SORTIES',
  statut_phase: 'Option',
  brief_creatif: '',
};

const KkpVenteNewsletterModal = ({
  show,
  onClose,
  onSaved,
  onDeleted,
  onError,
  meta,
  defaultCommercialId = '',
  initialValues = {},
  initialWizardStep,
  vente = null,
  onRequestBatValide,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const semaineTouchedRef = useRef(false);
  const { ask, modal } = useAppConfirm();
  const [deleting, setDeleting] = useState(false);
  const {
    currentStep,
    setCurrentStep,
    setSavedVente,
    piecesJointes,
    setPiecesJointes,
    activeVente,
    activeVenteId,
    shouldCloseAfterCreate,
  } = useKkpVenteBriefWizard({ show, vente, initialWizardStep, initialValues });

  const statutOptions = useMemo(
    () => filterStatutsForMonth(meta?.statuts, form.annee, form.mois),
    [meta?.statuts, form.annee, form.mois],
  );

  const semaineOptions = useMemo(
    () => filterSemaineOptionsForPeriod(
      generateNewsletterYearWeekOptions(Number(form.annee) || new Date().getFullYear()),
      {
        annee: form.annee,
        mois: form.mois,
        includeValue: activeVenteId ? form.semaine : null,
      },
    ),
    [form.annee, form.mois, form.semaine, activeVenteId],
  );

  useEffect(() => {
    if (!show) return;
    semaineTouchedRef.current = false;

    if (vente) {
      setForm({
        annee: vente.annee,
        mois: vente.mois,
        semaine: vente.semaine ?? '',
        commercial_id: vente.commercial_id ?? '',
        client_label: vente.client_label,
        entreprise_locale_id: vente.entreprise_locale_id ?? '',
        dolibarr_thirdparty_id: vente.dolibarr_thirdparty_id ?? '',
        emplacement: vente.emplacement ?? vente.format,
        type_news: vente.type_news ?? vente.contenu,
        statut_phase: vente.statut_phase,
        brief_creatif: venteBriefFromApi(vente.brief_encart),
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        annee: initialValues.annee ?? new Date().getFullYear(),
        mois: initialValues.mois ?? new Date().getMonth() + 1,
        emplacement: initialValues.emplacement ?? meta?.formats?.[0]?.value ?? 'NEWS SORTIES WE / BANDEAU TETE',
        type_news: initialValues.type_news ?? meta?.types_news?.[0] ?? 'SORTIES',
        statut_phase: initialValues.statut_phase ?? filterStatutsForMonth(meta?.statuts, initialValues.annee ?? new Date().getFullYear(), initialValues.mois ?? new Date().getMonth() + 1)[0]?.value ?? 'Option',
        ...initialValues,
        commercial_id: defaultCommercialId || '',
        brief_creatif: venteBriefFromApi(initialValues.brief_encart),
      });
    }
  }, [show, vente, initialValues, defaultCommercialId, meta]);

  useEffect(() => {
    if (!show || statutOptions.length === 0) return;
    if (!statutOptions.some((s) => s.value === form.statut_phase)) {
      setForm((f) => ({ ...f, statut_phase: statutOptions[0].value }));
    }
  }, [show, form.annee, form.mois, statutOptions]);

  useEffect(() => {
    if (!show || !form.semaine) return;
    const next = resolveKeptSemaine(form.semaine, semaineOptions, form.annee);
    if (next !== form.semaine) {
      setForm((f) => ({ ...f, semaine: next }));
    }
  }, [show, form.annee, form.mois, form.semaine, semaineOptions]);

  const markSemaineTouched = () => {
    semaineTouchedRef.current = true;
  };

  const buildPayload = () => {
    const storedSemaine = String(vente?.semaine ?? '').trim();
    const semaine = String(form.semaine ?? '').trim()
      || (semaineTouchedRef.current ? '' : storedSemaine);

    return {
      ...form,
      semaine,
      commercial_id: form.commercial_id || defaultCommercialId || null,
      entreprise_locale_id: form.entreprise_locale_id || null,
      dolibarr_thirdparty_id: form.dolibarr_thirdparty_id || null,
      brief_encart: briefCreatifToPayload(form.brief_creatif),
    };
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = buildPayload();
      if (activeVenteId) {
        const updated = await kkpService.updateVenteNewsletter(activeVenteId, payload);
        setSavedVente(updated);
        setPiecesJointes(briefPiecesJointesFromVente(updated));
        onSaved?.();
        onClose();
      } else {
        const created = await kkpService.createVenteNewsletter(payload);
        setSavedVente(created);
        setPiecesJointes(briefPiecesJointesFromVente(created));
        onSaved?.();
        if (shouldCloseAfterCreate(currentStep)) {
          onClose();
        }
      }
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!activeVenteId) return;
    if (!(await confirmVenteClear(ask, form.client_label))) return;

    setDeleting(true);
    try {
      await kkpService.clearVenteNewsletter(activeVenteId);
      onDeleted?.();
      onClose();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <AppModal
      show={show}
      title={activeVenteId ? 'Modifier la vente' : 'Nouvelle vente Newsletter'}
      onClose={onClose}
      size="lg"
      centered
      wrapBody={false}
      contentAs="form"
      contentProps={{ onSubmit: handleSave }}
    >

            <KkpVenteWizardSteps currentStep={currentStep} onStepChange={setCurrentStep} />

            <div className="kkp-vente-wizard-body">
              {currentStep === 0 && (
                <div className="row g-3">
                  <AppFormField className="col-md-4" label="Année" htmlFor="kkp-nl-annee" required>
                    <AppInput
                      id="kkp-nl-annee"
                      type="number"
                      required
                      value={form.annee}
                      onChange={(e) => {
                        markSemaineTouched();
                        setForm((f) => ({ ...f, annee: Number(e.target.value), semaine: '' }));
                      }}
                    />
                  </AppFormField>
                  <AppFormField className="col-md-4" label="Mois" htmlFor="kkp-nl-mois" required>
                    <AppSelect
                      id="kkp-nl-mois"
                      required
                      value={form.mois}
                      onChange={(e) => {
                        markSemaineTouched();
                        setForm((f) => ({ ...f, mois: Number(e.target.value), semaine: '' }));
                      }}
                    >
                      {(meta?.mois ?? []).map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-md-4" label="Semaine" required>
                    <KkpPlanningSemaineSelect
                      value={form.semaine}
                      onChange={(e) => {
                        markSemaineTouched();
                        setForm((f) => ({ ...f, semaine: e.target.value }));
                      }}
                      options={semaineOptions}
                      allowEmpty
                      emptyLabel={semaineOptions.length ? '— Choisir —' : '— Aucune semaine dispo —'}
                      required
                    />
                  </AppFormField>
                  <AppFormField className="col-12" label="Client">
                    <KkpClientAutocomplete
                      value={form.client_label}
                      onChange={(val) => setForm((f) => ({
                        ...f,
                        client_label: val,
                        entreprise_locale_id: '',
                        dolibarr_thirdparty_id: '',
                      }))}
                      onSelect={(client) => setForm((f) => ({
                        ...f,
                        client_label: client.nom ?? '',
                        dolibarr_thirdparty_id: Number(client?.id) > 0 ? Number(client.id) : '',
                        entreprise_locale_id: '',
                      }))}
                    />
                  </AppFormField>
                  <AppFormField className="col-md-6" label="Emplacement" htmlFor="kkp-nl-emplacement" required>
                    <AppSelect
                      id="kkp-nl-emplacement"
                      required
                      value={form.emplacement}
                      onChange={(e) => setForm((f) => ({ ...f, emplacement: e.target.value }))}
                    >
                      {(meta?.formats ?? []).map((f) => (
                        <option key={f.value} value={f.value}>{f.value}</option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-md-6" label="Type newsletter" htmlFor="kkp-nl-type" required>
                    <AppSelect
                      id="kkp-nl-type"
                      required
                      value={form.type_news}
                      onChange={(e) => setForm((f) => ({ ...f, type_news: e.target.value }))}
                    >
                      {(meta?.types_news ?? []).map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-md-6" label="Statut phase" htmlFor="kkp-nl-statut" required>
                    <AppSelect
                      id="kkp-nl-statut"
                      required
                      value={form.statut_phase}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (interceptLockedBatValideChange(next, form.statut_phase)) return;
                        if (interceptGraphisteBatValideRequest(next, form.statut_phase, activeVente, onRequestBatValide)) {
                          return;
                        }
                        setForm((f) => ({ ...f, statut_phase: next }));
                      }}
                    >
                      {statutOptions.map((s) => (
                        <option
                          key={s.value}
                          value={s.value}
                          title={batValideOptionTitle(s.value)}
                          style={isBatValideOptionLocked(s.value) ? { color: '#9ca3af' } : undefined}
                        >
                          {s.value}
                        </option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                </div>
              )}

              {currentStep === 1 && (
                <KkpVenteBriefCreativeStep
                  value={form.brief_creatif}
                  onChange={(value) => setForm((f) => ({ ...f, brief_creatif: value }))}
                  venteId={activeVenteId}
                  piecesJointes={piecesJointes}
                  onPiecesJointesChange={setPiecesJointes}
                  onError={onError}
                />
              )}
            </div>

            <KkpVenteWizardFooter
              currentStep={currentStep}
              onStepChange={setCurrentStep}
              onClose={onClose}
              onSave={handleSave}
              onDelete={handleClear}
              saving={saving}
              deleting={deleting}
              showDelete={Boolean(activeVenteId)}
              deleteLabel="Effacer"
              deletingLabel="Effacement…"
              deleteVariant="outline-warning"
              showCompleteLater={currentStep === 1}
            />
    </AppModal>
    {modal}
    </>
  );
};

export default KkpVenteNewsletterModal;
